import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useTheme } from '../../../contexts/ThemeContext';
import { getAvailableLocations } from '../../../utils/holidayService';
import Button from '../../../components/ui/Button';

const DisplayPreferences = ({ settings, memberColor, onChange, onColorChange }) => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [showColorPicker, setShowColorPicker] = useState(false);

  const predefinedColors = [
    '#EF4444', '#F97316', '#F59E0B', '#EAB308',
    '#84CC16', '#10B981', '#14B8A6', '#06B6D4',
    '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7',
    '#EC4899', '#F43F5E'
  ];
  
  const calendarViewOptions = [
    { value: 'day', label: 'Day View' },
    { value: 'week', label: 'Week View' },
    { value: 'month', label: 'Month View' }
  ];

  const taskSortOptions = [
    { value: 'priority', label: 'Priority' },
    { value: 'dueDate', label: 'Due Date' },
    { value: 'category', label: 'Category' },
    { value: 'assignee', label: 'Assignee' }
  ];

  const fontSizeOptions = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'xlarge', label: 'Extra Large' }
  ];

  const weekStartOptions = [
    { value: 'sunday', label: 'Sunday' },
    { value: 'monday', label: 'Monday' },
    { value: 'saturday', label: 'Saturday' }
  ];

  const locationOptions = getAvailableLocations()?.map(location => ({
    value: location,
    label: location
  }));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="palette" size={20} style={{ color: memberColor }} />
          Appearance
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-start gap-3">
              <Icon name={isDarkMode ? "moon" : "sun"} size={20} style={{ color: memberColor }} className="mt-1" />
              <div>
                <div className="font-medium text-foreground mb-1">Dark Mode</div>
                <div className="text-sm text-muted-foreground">
                  {isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
                </div>
              </div>
            </div>
            <button
              onClick={toggleDarkMode}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isDarkMode ? 'bg-primary' : 'bg-muted'
              }`}
              aria-label="Toggle dark mode"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isDarkMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="calendar" size={20} style={{ color: memberColor }} />
          Calendar Preferences
        </h3>
        <div className="space-y-4">
          <Select
            label="Default Calendar View"
            value={settings?.calendarView || 'month'}
            onChange={(value) => onChange({ calendarView: value })}
            options={calendarViewOptions}
            description="Your preferred view when opening the calendar"
          />
          <Select
            label="Week Starts On"
            value={settings?.weekStartsOn || 'sunday'}
            onChange={(value) => onChange({ weekStartsOn: value })}
            options={weekStartOptions}
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="map-pin" size={20} style={{ color: memberColor }} />
          Location & Holidays
        </h3>
        <div className="space-y-4">
          <Select
            label="Location"
            value={settings?.holidayLocation || 'United States'}
            onChange={(value) => onChange({ holidayLocation: value })}
            options={locationOptions}
            description="Select your location to display relevant holidays and school terms"
          />
          <Checkbox
            checked={settings?.showPublicHolidays !== false}
            onCheckedChange={(checked) => onChange({ showPublicHolidays: checked })}
            label="Show Public Holidays"
            description="Display public holidays on the family calendar"
          />
          <Checkbox
            checked={settings?.showSchoolTerms !== false}
            onCheckedChange={(checked) => onChange({ showSchoolTerms: checked })}
            label="Show School Terms"
            description="Display school terms and breaks on the family calendar"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="list" size={20} style={{ color: memberColor }} />
          Task List Organization
        </h3>
        <div className="space-y-4">
          <Select
            label="Default Task Sorting"
            value={settings?.taskListSort || 'priority'}
            onChange={(value) => onChange({ taskListSort: value })}
            options={taskSortOptions}
            description="How tasks are organized in your lists"
          />
          <Checkbox
            checked={settings?.showCompletedTasks || false}
            onCheckedChange={(checked) => onChange({ showCompletedTasks: checked })}
            label="Show Completed Tasks"
            description="Display completed tasks in your task lists"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="palette" size={20} style={{ color: memberColor }} />
          Color Theme
        </h3>
        <div className="p-4 bg-muted/30 rounded-lg border border-border relative">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-12 h-12 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: settings?.colorTheme || memberColor }}
              onClick={() => setShowColorPicker(!showColorPicker)}
              title="Click to change color"
            />
            <div>
              <div className="font-medium text-foreground">Your Assigned Color</div>
              <div className="text-sm text-muted-foreground">{settings?.colorTheme || memberColor}</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            This color is used to identify your tasks, calendar events, and messages throughout the app.
          </p>

          {/* Color Picker Dropdown */}
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-2 bg-popover border border-border rounded-lg p-3 shadow-warm-lg z-[200]">
              <div className="grid grid-cols-7 gap-2 mb-2">
                {predefinedColors?.map((color) => (
                  <button
                    key={color}
                    className="w-8 h-8 rounded-lg transition-transform hover:scale-110 active:scale-95 border-2 border-transparent hover:border-ring"
                    style={{ backgroundColor: color }}
                    onClick={() => {
                      onColorChange?.(color);
                      setShowColorPicker(false);
                    }}
                    aria-label={`Select color ${color}`}
                  />
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                fullWidth
                onClick={() => setShowColorPicker(false)}
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="accessibility" size={20} style={{ color: memberColor }} />
          Accessibility Settings
        </h3>
        <div className="space-y-4">
          <Select
            label="Font Size"
            value={settings?.fontSize || 'medium'}
            onChange={(value) => onChange({ fontSize: value })}
            options={fontSizeOptions}
            description="Adjust text size for better readability"
          />
          <Checkbox
            checked={settings?.highContrast || false}
            onCheckedChange={(checked) => onChange({ highContrast: checked })}
            label="High Contrast Mode"
            description="Increase contrast for better visibility"
          />
        </div>
      </div>
    </div>
  );
};

export default DisplayPreferences;